export interface TaskField {
  fieldName: string;
  formula?: string;
  value?: string;
}

export interface TaskViewSchema {
  /** 最低限の View 定義（旧UI互換） */
  fields?: TaskField[];
  /** ドキュメント準拠の where / mappings / orderBy 等を許容する */
  [key: string]: unknown;
}

export interface TaskConverter {
  target: {
    searchPath: string;
    baseObjectPath: string;
    httpMethod?: string;
  };
  viewSchema: TaskViewSchema;
}

export interface TaskActions {
  actionType: 'execute';
  converter: TaskConverter;
}

export interface TaskProfiles {
  actions: TaskActions;
}

export interface TaskConfig {
  id: string;
  name: string;
  wbsType: 'task';
  profiles: TaskProfiles;
}

export interface TaskFileResponse {
  resultCode: number;
  resultMessage: string;
  data: TaskConfig;
}

export type CsvFieldType = 'int' | 'string' | 'double' | 'float' | 'long' | 'boolean';

export interface CsvEntityField {
  fieldName: string;
  fieldType: CsvFieldType;
}

export interface CsvEntitySchema {
  contentsType: 'csv';
  headerCount: number;
  fields: CsvEntityField[];
}

export interface CsvTaskConverter {
  target: {
    searchPath: string;
    httpMethod: 'get' | 'post';
  };
  entitySchema: CsvEntitySchema;
  /** 取得結果の表示変換定義（任意） */
  viewSchema?: unknown;
  saveTarget: {
    savePath: string;
  };
}

export interface CsvTaskActions {
  actionType: 'execute';
  converter: CsvTaskConverter;
}

export interface CsvTaskProfiles {
  actions: CsvTaskActions;
}

export interface CsvTaskConfig {
  id: string;
  name: string;
  wbsType: 'task';
  profiles: CsvTaskProfiles;
}

export interface FixedEntityField {
  fieldName: string;
  length: number;
  trimType?: string;
  fieldType?: string;
  /** 同一レイアウトの繰り返し件数（配列フィールド用） */
  count?: number;
  /** 数値等の書式（例: "0", "D4"）— compiler_converter.md の Entity 項目オプション */
  format?: string;
  /** 固定長テキストの配置（例: "right"） */
  textAlignment?: string;
}

/** インライン定義のエンティティスキーマ（固定長） */
export interface FixedEntitySchemaInline {
  contentsType: 'fixed';
  charset: string;
  fields: FixedEntityField[];
}

/** 別ファイル参照のエンティティスキーマ（レイアウト定義のパス） */
export interface FixedEntitySchemaRef {
  schemaPath: string;
}

/** 固定長エンティティスキーマ（インライン or 別ファイル） */
export type FixedEntitySchema = FixedEntitySchemaInline | FixedEntitySchemaRef;

/** entitySchema が別ファイル参照かどうか */
export function isFixedEntitySchemaRef(schema: FixedEntitySchema): schema is FixedEntitySchemaRef {
  return 'schemaPath' in schema && typeof (schema as FixedEntitySchemaRef).schemaPath === 'string';
}

/**
 * Convert 出力スキーマ（固定長ファイルのレイアウト定義）
 * - ドキュメントでは convertSchema として登場する
 */
export type FixedConvertSchema = FixedEntitySchema;

export interface FixedViewSchema {
  fields: (string | { fieldName: string; formula: string })[];
}

export interface CrossJoinOptions {
  expandFields: string[];
  viewFields: Record<string, string>;
}

export interface SaveModel {
  id: string;
  wbsType: string;
  nodeObjects: string;
}

export interface FixedTaskConverter {
  target: {
    searchPath: string;
  };
  entitySchema: FixedEntitySchema;
  viewSchema: FixedViewSchema;
  crossJoinOptions: CrossJoinOptions;
  saveTarget: {
    savePath: string;
    saveModel: SaveModel;
  };
}

export interface FixedTaskActions {
  actionType: 'execute';
  converter: FixedTaskConverter;
}

export interface FixedTaskProfiles {
  actions: FixedTaskActions;
}

export interface FixedTaskConfig {
  id: string;
  name: string;
  wbsType: 'task';
  profiles: FixedTaskProfiles;
}

/** JSON 配列ファイルを固定長テキストへ変換するタスク用コンバータ（任意ブロックは実行時のみ付与） */
export interface JsonToFixedTaskConverter {
  target: {
    searchPath: string;
  };
  /** 出力レイアウト（JSON→固定長のため entitySchema ではなく convertSchema を使用） */
  convertSchema: FixedConvertSchema;
  viewSchema?: FixedViewSchema;
  crossJoinOptions?: CrossJoinOptions;
  saveTarget: {
    savePath: string;
    saveModel?: SaveModel;
  };
}

export interface JsonToFixedTaskActions {
  actionType: 'execute';
  converter: JsonToFixedTaskConverter;
}

export interface JsonToFixedTaskProfiles {
  actions: JsonToFixedTaskActions;
}

export interface JsonToFixedTaskConfig {
  id: string;
  name: string;
  wbsType: 'task';
  profiles: JsonToFixedTaskProfiles;
}
